import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  InputField,
  Table,
  CheckBox as BaseCheckBox,
  ContextMenu
} from '@ubeya/shared-web/components';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import usePayrollFields from '@ubeya/shared/hooks/account/usePayrollFields';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import useTranslatedOptions from '@ubeya/shared/hooks/useTranslatedOptions';
import { generatePositionWageValues } from '@ubeya/shared-web/utils/positions';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import RateModal from './RateModal';
import SharedWithClientToggleRenderer from './SharedWithClientToggleRenderer';

const Wrapper = styled(FlexColumn)`
  flex: 1;
  padding: 31px 50px;
  overflow: auto;
`;

// flex value depends on number of columns so that select all checkbox is aligned with the last column
const Description = styled(Flex)`
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 500;
  flex: ${({ $showSharePositionColumn, $showFlatWage }) =>
    3 + ($showSharePositionColumn ? 0 : 1) + ($showFlatWage ? 4 : 0)};
`;

const CheckBox = styled(BaseCheckBox)`
  flex: 1;
`;

const HeaderWrapper = styled(FlexCenter)`
  justify-content: space-between;
`;

const OvertimeContainer = styled(Flex)`
  gap: 20px;
  width: 50%;
`;

const PayrollFields = styled.div`
  margin-top: 30px;
`;
const PayrollField = styled.div`
  width: 50%;
`;

const Container = styled(FlexColumn)`
  flex: 1;
  min-width: 500px;
  .data-grid {
    margin-bottom: 25px;
  }
`;

const ActionRenderer = ({ rowData, rowData: { id }, columnData: { handleEdit } }) => {
  const { t } = useContext(ConfigContext);

  const options = useTranslatedOptions([{ label: t('edit'), onClick: () => handleEdit(rowData), visible: id }]);

  return <ContextMenu options={options} usePortal />;
};

const Rates = () => {
  const { t } = useContext(ConfigContext);
  const { positionsOptions, mappedPositions } = usePositions();
  const { account } = useAccountConfig();
  const myPrivileges = useContext(PrivilegesContext);

  const { isUbeyaAdmin } = useUser();

  const hasAmendPayRatePrivilege = !!myPrivileges[privileges.AMEND_PAY_RATE];

  const { dailyOvertimeIsActive = false, dailyOvertimeRate, doubleDailyOvertimeRate } = account?.payrollConfig || {};

  const { fields: accountPayrollFields } = usePayrollFields();

  const [editRate, setEditRate] = useState();

  const {
    input: { value: positionsWages, onChange: onChangePositionsWages }
  } = useField('positionsWages');

  const {
    input: { value: isClientControlsRates }
  } = useField('isClientControlsRates');

  const showSharePositionColumn = isUbeyaAdmin || !isClientControlsRates;

  const {
    input: { value: excludedPositions, onChange: onChangeExcludedPositions }
  } = useField('excludedPositions');

  const {
    input: { value: baseWage, onChange: onChangeBaseWage }
  } = useField('wage');

  const {
    input: { value: basePayrollWage, onChange: onChangeBasePayrollWage }
  } = useField('payrollWage');

  const handleChangeExcludedPositions = useCallback(
    (positionId, value) => {
      const excludedPositionsUpdated = value
        ? excludedPositions.filter((id) => id !== positionId)
        : [...excludedPositions, positionId];

      onChangeExcludedPositions(excludedPositionsUpdated);
    },
    [excludedPositions, onChangeExcludedPositions]
  );

  const positionsWithRates = useMemo(
    () =>
      positionsOptions.map(({ value }) => {
        const currentPositionWageData = (positionsWages || []).find(({ positionId }) => positionId === value);

        const selectedWage = generatePositionWageValues({
          wage: { ...(currentPositionWageData?.wage || { hourlyWage: null, flatWage: null, flatHours: null }) },
          wages: currentPositionWageData?.wages || [],
          externalWage: {
            ...(currentPositionWageData?.payrollWage || { hourlyWage: null, flatWage: null, flatHours: null })
          },
          externalWageKey: 'payrollWage'
        });

        const isSharedWithClient = !excludedPositions.includes(value);

        return {
          // FOR TABLE
          wage: selectedWage.wage,
          payrollWage: selectedWage.payrollWage,

          // FOR MODAL
          wages: currentPositionWageData?.wages || [],
          positionId: value,
          isSharedWithClient
        };
      }),
    [excludedPositions, positionsWages, positionsOptions]
  );

  const groupedData = useMemo(() => {
    return [{ wage: { ...baseWage }, payrollWage: { ...basePayrollWage } }, ...positionsWithRates];
  }, [basePayrollWage, baseWage, positionsWithRates]);

  const handleChangeAllPositionsToNotShared = useCallback(
    () =>
      onChangeExcludedPositions([
        ...excludedPositions,
        ...groupedData.map(({ positionId }) => positionId).filter((curr) => !!curr)
      ]),
    [excludedPositions, groupedData, onChangeExcludedPositions]
  );

  const handleChangeAllPositionsToShared = useCallback(() => onChangeExcludedPositions([]), [
    onChangeExcludedPositions
  ]);

  const formattingMap = useFormattingMap();

  const isFlatWage = account?.payrollConfig?.flatIsActive;
  const showFlatWage = isFlatWage && !isClientControlsRates;

  const wageFormatter = useCallback(
    ({ value, currency = true }) => (currency ? formattingMap.currency(value) : value),
    [formattingMap]
  );

  const columns = useMemo(
    () => [
      {
        Header: t('position'),
        dataKey: 'positionId',
        resizable: true,
        maxWidth: 450,
        formatter: (value) => (value ? mappedPositions[value]?.label : t('baseRate'))
      },
      {
        Header: t('clientWage'),
        dataKey: 'wage.hourlyWage',
        formatter: (value) => wageFormatter({ value })
      },
      {
        Header: t('specificClientPayRate'),
        dataKey: 'payrollWage.hourlyWage',
        formatter: (value) => wageFormatter({ value })
      },
      ...(showFlatWage
        ? [
            {
              Header: t('flatWage'),
              dataKey: 'payrollWage.flatWage',
              formatter: (value) => wageFormatter({ value })
            },
            {
              Header: t('flatHours'),
              dataKey: 'payrollWage.flatHours',
              formatter: (value) => wageFormatter({ value, currency: false })
            },
            {
              Header: t('globalWage'),
              dataKey: 'wage.flatWage',
              formatter: (value) => wageFormatter({ value })
            },
            {
              Header: t('flatBillRateHoursLimit'),
              dataKey: 'wage.flatHours',
              formatter: (value) => wageFormatter({ value, currency: false })
            }
          ]
        : []),
      showSharePositionColumn && {
        Header: t('positionSharedWithClient'),
        dataKey: 'isSharedWithClient',
        Cell: SharedWithClientToggleRenderer,
        columnData: { onChange: handleChangeExcludedPositions },
        captureRowClick: false
      }
    ],

    [t, showFlatWage, showSharePositionColumn, handleChangeExcludedPositions, mappedPositions, wageFormatter]
  );

  const handleAddNewPosition = useCallback(
    ({ positionId, wage = {}, payrollWage = {}, wages = [] }) => {
      const newPositionData = {
        status: 'added',
        positionId,

        // ID INSIDE EACH ITEM IS NOT SEND
        wages,

        // ID IS SENT
        wage: { ...wage, id: -Date.now() },
        payrollWage: { ...payrollWage, id: -Date.now() }
      };
      onChangePositionsWages([...positionsWages, newPositionData]);
    },
    [onChangePositionsWages, positionsWages]
  );

  const handleEditPosition = useCallback(
    ({ positionId, wage, payrollWage, wages = [] }) => {
      onChangePositionsWages(
        positionsWages.map((item) =>
          item.positionId === positionId
            ? {
                ...item,
                status: 'edited',
                wages,
                wage: wage ? { ...item.wage, ...wage } : item.wage,
                payrollWage: payrollWage ? { ...item.payrollWage, ...payrollWage } : item.payrollWage
              }
            : item
        )
      );
    },
    [onChangePositionsWages, positionsWages]
  );

  const onConfirm = useCallback(
    (values) => {
      if (!editRate) {
        return;
      }

      // BASE RATE
      if (!editRate.positionId) {
        const { wage, payrollWage } = values;
        if (wage) {
          onChangeBaseWage(wage);
        }
        if (payrollWage) {
          onChangeBasePayrollWage(payrollWage);
        }
        return;
      }

      // PER POSITION RATE
      const matchingPosition = positionsWages.find(
        ({ positionId: currPositionId }) => currPositionId === values.positionId
      );

      const { positionId, wage, payrollWage, wages } = values;

      if (!matchingPosition) {
        handleAddNewPosition({ positionId, wage, payrollWage, wages });
        return;
      }

      handleEditPosition({ positionId, wage, payrollWage, wages });
      return;
    },
    [editRate, handleAddNewPosition, handleEditPosition, onChangeBasePayrollWage, onChangeBaseWage, positionsWages]
  );

  const disableEditRates = isClientControlsRates || !hasAmendPayRatePrivilege;

  return (
    <Wrapper>
      <HeaderWrapper>
        <Description $showSharePositionColumn={showSharePositionColumn} $showFlatWage={showFlatWage}>
          {t('ratesTabDescription')}
        </Description>
        {showSharePositionColumn && (
          <CheckBox
            label={t('selectAll')}
            value={excludedPositions.length === 0}
            name="selectAll"
            onChange={(val) => (val ? handleChangeAllPositionsToShared() : handleChangeAllPositionsToNotShared())}
          />
        )}
      </HeaderWrapper>
      <Container>
        <Table
          headerRowHeight={50}
          autoHeight
          columns={columns}
          data={groupedData}
          shadow={false}
          onRowClick={disableEditRates ? null : ({ rowData }) => setEditRate(rowData)}
          actionRenderer={({ rowData }) =>
            !disableEditRates && (
              <ActionRenderer
                rowData={rowData}
                columnData={{
                  handleEdit: setEditRate
                }}
              />
            )
          }
        />

        {dailyOvertimeIsActive && hasAmendPayRatePrivilege && (
          <>
            <Description>{t('setOvertimeMultipliers')}</Description>
            <OvertimeContainer>
              <InputField
                placeholder={dailyOvertimeRate}
                name="payrollConfig.dailyOvertimeRate"
                title={t('dailyOvertimeRate')}
                type="number"
              />
              <InputField
                placeholder={doubleDailyOvertimeRate}
                name="payrollConfig.doubleDailyOvertimeRate"
                title={t('doubleDailyOvertimeRate')}
                type="number"
              />
            </OvertimeContainer>
          </>
        )}
        <PayrollFields>
          <Description>{t('setSpecificPayrollFields')}</Description>
          {accountPayrollFields
            .filter(({ isClient, deletedAt }) => !!isClient && !deletedAt)
            .map(({ id, slug }) => (
              <PayrollField key={id}>
                <InputField name={`payrollFields[payroll-${id}].value`} title={t(slug)} type="number" />
              </PayrollField>
            ))}
        </PayrollFields>
      </Container>

      {editRate && (
        <RateModal
          isFlatWage={showFlatWage}
          selectedRate={editRate}
          mappedPositions={mappedPositions}
          onClose={() => setEditRate()}
          onConfirm={onConfirm}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(Rates);
